import { motion } from 'framer-motion';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useState, useEffect } from 'react';
import Button from './Button';
import Script from 'next/script';

export default function LocationMap({ event, park, className = '' }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);

  const { latitude, longitude, location, meetingpoint, parkfullname } = event;

  // Use park coordinates if available, otherwise fall back to event coordinates
  const parkLat =
    park && park[0]?.latitude ? parseFloat(park[0].latitude) : null;
  const parkLng =
    park && park[0]?.longitude ? parseFloat(park[0].longitude) : null;

  const eventLocation = {
    lat: parkLat || parseFloat(latitude) || 39.8283, // Park coords first, then event coords, then US center
    lng: parkLng || parseFloat(longitude) || -98.5795,
  };

  // Initialize Google Maps when script loads
  useEffect(() => {
    if (
      googleMapsLoaded &&
      typeof window !== 'undefined' &&
      window.google &&
      window.google.maps
    ) {
      initializeMap();
    }
  }, [googleMapsLoaded, eventLocation.lat, eventLocation.lng, parkfullname]);

  // Fallback: Check if Google Maps is already loaded
  useEffect(() => {
    const checkGoogleMaps = () => {
      if (
        typeof window !== 'undefined' &&
        window.google &&
        window.google.maps &&
        !googleMapsLoaded
      ) {
        setGoogleMapsLoaded(true);
      }
    };

    const handleGoogleMapsLoaded = () => {
      setGoogleMapsLoaded(true);
    };

    // Check immediately
    checkGoogleMaps();

    // Check after a short delay in case script loads asynchronously
    const timer = setTimeout(checkGoogleMaps, 1000);

    // Listen for the fallback script load event
    if (typeof window !== 'undefined') {
      window.addEventListener('googleMapsLoaded', handleGoogleMapsLoaded);
    }

    return () => {
      clearTimeout(timer);
      if (typeof window !== 'undefined') {
        window.removeEventListener('googleMapsLoaded', handleGoogleMapsLoaded);
      }
    };
  }, [googleMapsLoaded]);

  const initializeMap = () => {
    if (
      typeof window === 'undefined' ||
      !window.google ||
      !window.google.maps
    ) {
      return;
    }

    const mapElement = document.getElementById('map');
    if (!mapElement) {
      return;
    }

    const map = new window.google.maps.Map(mapElement, {
      center: eventLocation,
      zoom: 14,
      ...mapOptions,
    });

    // Add a marker for the event location
    new window.google.maps.Marker({
      position: eventLocation,
      map: map,
      title: parkfullname || 'Event Location',
      icon: {
        url:
          'data:image/svg+xml;charset=UTF-8,' +
          encodeURIComponent(`
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#059669"/>
                    </svg>
                `),
        scaledSize: new window.google.maps.Size(24, 24),
        anchor: new window.google.maps.Point(12, 12),
      },
    });
  };

  const mapContainerStyle = {
    width: '100%',
    height: '300px',
  };

  const mapOptions = {
    zoomControl: true,
    mapTypeControl: false,
    scaleControl: true,
    streetViewControl: false,
    rotateControl: false,
    fullscreenControl: true,
    styles: [
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }],
      },
    ],
  };

  const getDirectionsUrl = () => {
    const destination = `${eventLocation.lat},${eventLocation.lng}`;
    return `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
  };

  const getGoogleMapsUrl = () => {
    return `https://www.google.com/maps?q=${eventLocation.lat},${eventLocation.lng}`;
  };

  // Safety check for event data
  if (!event) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className={`bg-white rounded p-6 ${className}`}
      >
        <div className="flex items-center gap-3 mb-4">
          <h3 className="text-xl font-semibold text-brown-200">Location</h3>
        </div>
        <div className="animate-pulse">
          <div className="h-64 bg-beige-100 rounded-lg"></div>
        </div>
      </motion.div>
    );
  }

  if (!isLoaded) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className={`bg-white rounded p-6 ${className}`}
      >
        <div className="flex items-center gap-3 mb-4">
          <h3 className="text-xl font-semibold text-brown-200">Location</h3>
        </div>
        <div className="animate-pulse">
          <div className="h-64 bg-beige-100 rounded-lg"></div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.3 }}
      className={`bg-white rounded p-6 ${className}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-xl font-semibold text-brown-200">Location</h3>
        </div>

        <div className="flex gap-2">
          <Button
            href={getDirectionsUrl()}
            target="_blank"
            rel="noreferrer"
            variant="primary"
            size="lg"
            icon={<ArrowTopRightOnSquareIcon className="w-4 h-4" />}
          >
            Get Directions
          </Button>
        </div>
      </div>

      {/* Location Details */}
      {(location || meetingpoint || parkfullname) && (
        <div className="mb-4">
          {parkfullname && (
            <p className="text-sm text-brown-200 mb-2">
              <span className="font-black">Park:</span>{' '}
              <a
                href={getGoogleMapsUrl()}
                target="_blank"
                rel="noreferrer"
                className="text-green-100 hover:text-green-100/80 transition-colors underline"
              >
                {parkfullname}
                <ArrowTopRightOnSquareIcon className="w-3 h-3 inline ml-1" />
              </a>
            </p>
          )}
          {location && (
            <p className="text-sm text-brown-200 mb-2">
              <span className="font-black">Location:</span> {location}
            </p>
          )}
          {meetingpoint && (
            <p className="text-sm text-brown-200">
              <span className="font-black">Meeting Point:</span> {meetingpoint}
            </p>
          )}
        </div>
      )}

      {/* Google Maps Embed */}
      <div className="relative">
        <Script
          src={`https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}&libraries=geometry`}
          strategy="afterInteractive"
          onLoad={() => {
            setGoogleMapsLoaded(true);
          }}
          onError={(error) => {
            console.error('Google Maps script failed to load:', error);
            setGoogleMapsLoaded(false);
          }}
        />

        {/* Fallback script loading */}
        {!googleMapsLoaded && (
          <script
            dangerouslySetInnerHTML={{
              __html: `
                                if (!window.google) {
                                    const script = document.createElement('script');
                                    script.src = 'https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}&libraries=geometry';
                                    script.onload = function() {
                                        window.dispatchEvent(new Event('googleMapsLoaded'));
                                    };
                                    script.onerror = function() {
                                        console.error('Fallback Google Maps script failed to load');
                                    };
                                    document.head.appendChild(script);
                                }
                            `,
            }}
          />
        )}

        {googleMapsLoaded ? (
          <div
            id="map"
            className="h-64 w-full rounded-lg border border-beige-100"
            style={{ minHeight: '256px' }}
          />
        ) : (
          // Fallback when Google Maps is not available
          <div className="h-64 bg-beige-100 rounded-lg border border-beige-100 flex items-center justify-center">
            <div className="text-center">
              <p className="text-sm text-brown-200 font-medium mb-2">
                Loading Map...
              </p>
              <p className="text-xs text-brown-100 mb-3">
                Use the &quot;Get Directions&quot; button above to open Google
                Maps
              </p>
              <Button
                href={getGoogleMapsUrl()}
                target="_blank"
                rel="noreferrer"
                variant="primary"
                size="lg"
                icon={<ArrowTopRightOnSquareIcon className="w-3 h-3" />}
              >
                View in Google Maps
              </Button>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
